import { handleApiError, handleApiResponse } from "@/methods/api";
import { renumberQuestionSerialsAcrossSections, toActivitySectionInput } from "@/methods/activity";
import ApiVerified from "@/services/apiVerified";
import { ApiResponse, IdNamePair } from "@/types/global";
import {
  PastYearActivityInputs,
  PastYearPaper,
  PastYearPaperInputs,
  PastYearPaperUpdate,
  PastYearQuestionPaper,
  PastYearQuestionPaperUpdate,
} from "@/types/pastYearPaper";

export default class PastYearPaperApi extends ApiVerified {
  async getPastYearQuestionPapersForStudent(
    userId: number,
    courseId: number,
    subjectId: number | null,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<PastYearQuestionPaper>>(
        `past-year-paper/past-year-question-papers-for-student`,
        { params: { userId: userId, courseId: courseId, subjectId: subjectId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getPastYearPapers(onlyActive: boolean): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `past-year-paper/get-past-year-papers`,
        { params: { onlyActive: onlyActive } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getDetailedPastYearPaper(pastYearPaperId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<PastYearPaper | null>(
        `past-year-paper/get-detailed-past-year-paper`,
        { params: { pastYearPaperId: pastYearPaperId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getPastYearQuestionPapers(
    pastYearPaperId: number,
    onlyActive: boolean,
    onlyUntagged: boolean,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `past-year-paper/get-past-year-question-papers`,
        {
          params: {
            pastYearPaperId: pastYearPaperId,
            onlyActive: onlyActive,
            onlyUntagged: onlyUntagged,
          },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getDetailedPastYearQuestionPapers(
    pastYearPaperId: number,
    onlyActive: boolean,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<PastYearQuestionPaper>>(
        `past-year-paper/detailed-past-year-question-papers`,
        { params: { pastYearPaperId: pastYearPaperId, onlyActive: onlyActive } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async createPastYearPaper(pastYearPaperInputs: PastYearPaperInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `past-year-paper/create-past-year-paper`,
        pastYearPaperInputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updatePastYearPaper(pastYearPaperInputs: PastYearPaperUpdate): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `past-year-paper/update-past-year-paper`,
        pastYearPaperInputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updatePastYearQuestionPaper(
    pastYearQuestionPaperInputs: PastYearQuestionPaperUpdate,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `past-year-paper/update-past-year-question-paper`,
        pastYearQuestionPaperInputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async createPastYearPaperActivity(newActivity: PastYearActivityInputs): Promise<ApiResponse> {
    try {
      // AR-7.3: strip each section's Questions to the wire rows at the save boundary, matching
      // create-activity / save-activity-questions.
      const wireBody = {
        ...newActivity,
        Sections: renumberQuestionSerialsAcrossSections(
          newActivity.Sections.map(toActivitySectionInput),
        ),
      };
      const response = await this.axiosInstance.post<number>(
        `past-year-paper/create-past-year-paper-activity`,
        wireBody,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }
}
